/**
 * WizardRail — Roomgrid step rail (ported from the locks-management
 * account/key wizards): a 236px column on the sunken surface listing the
 * numbered steps (✓ tile when done, teal-weak tile when active), plus a
 * live summary pinned at the bottom.
 */

import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";

export interface WizardRailStep {
  key: string;
  title: string;
  desc?: string;
}

export interface WizardRailProps {
  steps: WizardRailStep[];
  current: number;
  /** Click any step to jump to it — validation happens on Save. */
  onStepClick?: (index: number) => void;
  summaryTitle?: string;
  summaryMeta?: string;
}

export const WizardRail: React.FC<WizardRailProps> = ({
  steps,
  current,
  onStepClick,
  summaryTitle,
  summaryMeta,
}) => {
  const theme = useTheme();
  const rg = theme.custom.rg;

  return (
    <Box
      sx={{
        width: 236,
        flex: "none",
        backgroundColor: rg.bg2,
        borderRight: `1px solid ${rg.border}`,
        p: "16px 14px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        overflowY: "auto",
      }}
    >
      {steps.map((step, idx) => {
        const done = idx < current;
        const active = idx === current;
        const clickable = !active && Boolean(onStepClick);
        return (
          <Box
            key={step.key}
            onClick={clickable ? () => onStepClick?.(idx) : undefined}
            sx={{
              display: "flex",
              gap: "10px",
              p: "9px 10px",
              borderRadius: "9px",
              backgroundColor: active ? rg.bgElev : "transparent",
              cursor: clickable ? "pointer" : "default",
              "&:hover": clickable ? { backgroundColor: rg.bgElev } : undefined,
            }}
          >
            <Box
              component="span"
              className="rg-mono"
              sx={{
                width: 24,
                height: 24,
                flex: "none",
                display: "grid",
                placeItems: "center",
                borderRadius: "7px",
                fontSize: "11px",
                fontWeight: 600,
                border: `1px solid ${
                  active || done ? rg.accent : rg.borderStrong
                }`,
                backgroundColor: done
                  ? rg.accent
                  : active
                  ? rg.accentWeak
                  : "transparent",
                color: done ? "#ffffff" : active ? rg.accent : rg.text3,
              }}
            >
              {done ? <CheckIcon sx={{ fontSize: 13 }} /> : String(idx + 1).padStart(2, "0")}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1px", minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: active ? rg.text : rg.text2,
                  lineHeight: 1.3,
                }}
              >
                {step.title}
              </Typography>
              {step.desc && (
                <Typography sx={{ fontSize: "10.5px", color: rg.text3, lineHeight: 1.35 }}>
                  {step.desc}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}

      {(summaryTitle || summaryMeta) && (
        <Box
          sx={{
            mt: "auto",
            pt: "12px",
            px: "10px",
            borderTop: `1px solid ${rg.border}`,
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <Typography
            sx={{
              fontSize: "10.5px",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontWeight: 600,
              color: rg.text3,
            }}
          >
            Summary
          </Typography>
          {summaryTitle && (
            <Typography sx={{ fontSize: "12px", color: rg.text2 }}>
              {summaryTitle}
            </Typography>
          )}
          {summaryMeta && (
            <Typography className="rg-mono" sx={{ fontSize: "10.5px", color: rg.text3 }}>
              {summaryMeta}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default WizardRail;
